import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { authApi } from '@/api'
// UserRead type is used implicitly via API response
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'
import { assertPersonalSession, personalSession, requirePersonalSession } from '../personal-session'
import { normalizeTonAddress } from '@/utils/ton-address'

interface UseCurrentUserOptions {
  enabled?: MaybeRefOrGetter<boolean | undefined>
}

export function useCurrentUser(options: UseCurrentUserOptions = {}) {
  const { enabled } = options
  const queryEnabled = computed(() => Boolean(personalSession.value) && (toValue(enabled) ?? true))

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: computed(() => ['auth', 'user', personalSession.value?.id ?? null]),
    queryFn: async ({ queryKey }) => {
      const session = requirePersonalSession()
      if (queryKey[2] !== session.id) throw new Error('Wallet session changed.')
      const response = await authApi.getCurrentUser()
      assertPersonalSession(session)
      if (response.success && response.data) {
        if (normalizeTonAddress(response.data.wallet) !== session.wallet) {
          throw new Error('Wallet session does not match the account. Please reconnect.')
        }
        return { sessionId: session.id, value: response.data }
      }
      throw new Error(response.error ?? 'Failed to fetch current user')
    },
    refetchInterval: POLLING_INTERVALS.BACKEND_API,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.SHORT,
    enabled: queryEnabled,
  })

  return {
    currentUser: computed(() =>
      queryEnabled.value && data.value?.sessionId === personalSession.value?.id
        ? data.value?.value
        : undefined
    ),
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load user data'),
    refetch: async () => {
      if (queryEnabled.value) return refetch()
    },
  }
}
